/**
 * The module catalogue (spec §3.1) and the scope vocabulary (spec §3.3).
 *
 * These two tables are what a module and a scope are *called*, and which module
 * has to be on for which scope to be grantable. They decide nothing: the store
 * owns every verb, `capabilitiesFor` (in `tenant.ts`) owns the projection the
 * partner reads, and `provider-api` computes authorization as
 * `key.scopes ∩ scopes(tenant.modules)` from rows rather than from this file.
 * What lives here is the vocabulary all of that is written in.
 *
 * It is in `packages/domain` because three surfaces need it and none of them may
 * be the authority for the others:
 *
 *  * **`apps/cli`** — the operator surface where a human types `calenar`, and so
 *    the place a typo has to be caught before it becomes a row.
 *  * **`apps/admin-api`** — the same refusals, over HTTP, for the console.
 *  * **`apps/admin`** — offering the choices: a freshly created tenant has no
 *    `tenant_modules` rows at all, so the wizard's module step has nothing on the
 *    server to label, and the scope picker disables what is not grantable
 *    *before* the click (the server's refusal stays the authority).
 *
 * It lived in all three at once for three changes, each copy naming this move as
 * its successor. This is that move; there is now one table, and the drift it was
 * heading for is not available any more.
 */
/** Exactly spec §3.1's table, in its order. */
export const MODULES = [
    {
        key: 'calendar',
        kind: 'foundation',
        dependsOn: ['clients', 'services'],
        label: 'Calendar and appointments',
    },
    { key: 'clients', kind: 'foundation', dependsOn: [], label: 'Client book' },
    { key: 'services', kind: 'foundation', dependsOn: [], label: 'Service catalog' },
    { key: 'sales', kind: 'add-on', dependsOn: ['clients'], label: 'Checkout and sales' },
    { key: 'products', kind: 'add-on', dependsOn: ['sales'], label: 'Products and retail' },
    { key: 'certificates', kind: 'add-on', dependsOn: ['sales'], label: 'Gift certificates' },
    { key: 'credit', kind: 'add-on', dependsOn: ['clients'], label: 'Account credit' },
    { key: 'notifications', kind: 'add-on', dependsOn: ['clients'], label: 'Notifications' },
    { key: 'reporting', kind: 'add-on', dependsOn: [], label: 'Reporting' },
    { key: 'billing', kind: 'add-on', dependsOn: ['sales'], label: 'Billing and payments' },
    {
        key: 'booking_site',
        kind: 'add-on',
        dependsOn: ['calendar', 'clients', 'notifications'],
        label: 'Guest booking site',
    },
    { key: 'ordering', kind: 'add-on', dependsOn: ['clients', 'notifications'], label: 'Ordering' },
    {
        key: 'departures',
        kind: 'add-on',
        dependsOn: ['calendar', 'clients'],
        label: 'Departures',
    },
    /**
     * Lodging (spec §4.7) — the stay mode, the rate calendar, the tiered
     * cancellation fee and the OTA boundary.
     *
     * `calendar` + `clients`, exactly as `departures` depends: a stay *is* a
     * time-claim in the book against a guest, and a tenant with neither has
     * nowhere to put one. It is deliberately **not** dependent on `billing`,
     * although §4.7 says "lodging always takes a deposit": a deposit is money and
     * money is `sales`/`billing`'s business, but a hotel that has not yet
     * connected a payment rail must still be able to hold a room — refusing to
     * enable the module until the money works would make the dependency DAG
     * enforce a sales policy rather than a data one.
     */
    { key: 'lodging', kind: 'add-on', dependsOn: ['calendar', 'clients'], label: 'Lodging' },
    /**
     * Tour photos (`docs/tour-photos-design.md`) — the studio, the code, the album.
     *
     * **Depends on `booking_site`, and on nothing else.** That is the one real
     * decision in this row, and it took ruling two other candidates out:
     *
     *  * *`departures`* is the obvious guess and is wrong. Decision 2 makes the
     *    sailing anchor **nullable** because "the real studio creates ad-hoc
     *    albums, and the platform must not pretend otherwise" — the reference
     *    implementation has no bookings at all. A dependency here would refuse the
     *    module to the exact business it was designed from.
     *  * *`clients`* is wrong for the same reason one level down: the family
     *    anchor is nullable too, and a photographer handing a card to somebody
     *    whose name they never learned is the ordinary case, not the edge.
     *
     * `booking_site` earns it. Decision 6: "The guest claims on the tenant's own
     * site" — `/photos` is a page route on `guest-api`, resolved from the Host
     * header, and a tenant with no `booking_site` has no hostname for a code to be
     * typed into. Enabling `photos` without it would create albums nobody could
     * open, which is precisely the half-working state §3.1's dependency check
     * exists to prevent.
     *
     * There is deliberately **no `photos:*` scope** below. A scope exists to be
     * granted to a partner key; decision 9 says the partner wire is untouched, and
     * a grantable scope with no route behind it is a declaration shipped ahead of
     * its agreement.
     */
    {
        key: 'photos',
        kind: 'add-on',
        dependsOn: ['booking_site'],
        label: 'Tour photos',
    },
    /**
     * Merch and prints (`docs/fulfillment-design.md`) — a catalog of things
     * somebody else makes, an order that ends at a factory, and a parcel.
     *
     * **`products` + `billing`, and decision 1 says why in one line:** "stocked
     * items and a payment rail are what it *fulfils*." Both are load-bearing
     * rather than tidy:
     *
     *  * *`products`* because a synced t-shirt lands as an `items` row of kind
     *    `stocked` with an `item_fulfillment` satellite. A tenant without the
     *    retail catalog would have the sync writing rows into a module they have
     *    not got, which is the half-working state §3.1's dependency check exists
     *    to prevent.
     *  * *`billing`* because — unlike a restaurant ticket, which a counter can
     *    settle in cash — **nothing is submitted until the money has landed**.
     *    The whole lane starts at `paid` (see `ORDER_TRANSITIONS`), and a tenant
     *    with no payment rail would be a shop whose orders can never leave
     *    `placed`. `ordering` is deliberately *not* a dependency: an order is a
     *    core concept here, not that module's property, and requiring the
     *    restaurant module to sell a hoodie would be a dependency on a table
     *    rather than on a capability.
     *
     * The guest shop needs `booking_site` as well, and that is checked **where
     * the pages mount** rather than declared here — the `photos` precedent, and
     * its reason: a tenant may run merch through the staff surface and the
     * operator CLI with no public site at all, and a dependency would refuse the
     * module to that business.
     */
    {
        key: 'fulfillment',
        kind: 'add-on',
        dependsOn: ['products', 'billing'],
        label: 'Merch and prints',
    },
    { key: 'partner_api', kind: 'add-on', dependsOn: ['calendar'], label: 'Partner API' },
    { key: 'admin', kind: 'platform', dependsOn: [], label: 'Operator console' },
];
export const MODULE_KEYS = MODULES.map((m) => m.key);
export function moduleSpec(key) {
    return MODULES.find((m) => m.key === key);
}
/** The human name, falling back to the key — a label is never a reason to fail. */
export function moduleLabel(key) {
    return moduleSpec(key)?.label ?? key;
}
/**
 * Which of `module`'s dependencies are missing from `enabled`.
 *
 * Evaluated against the state the caller would *leave behind*, not the state it
 * found: enabling `products` and `sales` in one call has to succeed, and
 * checking against the old set would refuse it (spec §3.1 — the dependency is
 * validated when a module is enabled, and half-working is what it exists to
 * prevent).
 */
export function missingDependencies(enabled, module) {
    const spec = moduleSpec(module);
    if (spec === undefined)
        return [];
    return spec.dependsOn.filter((d) => !enabled.has(d));
}
/** Which enabled modules would be left depending on `module` if it went off. */
export function dependentsOf(enabled, module) {
    return MODULES.filter((m) => enabled.has(m.key) && m.dependsOn.includes(module)).map((m) => m.key);
}
/**
 * `module:action`, and **never granted by default** (spec §3.3).
 *
 * A tenant's modules say what the business has; they must not decide what a key
 * can reach. The rows below the first six are the future facets the spec names,
 * present so that the day one is sold the answer is a scope that already has a
 * module attached rather than an ad-hoc string.
 */
export const SCOPES = [
    { scope: 'catalog:read', module: 'services', grants: 'the service menu' },
    { scope: 'calendar:read', module: 'calendar', grants: 'openings, never the guest attached' },
    { scope: 'calendar:write', module: 'calendar', grants: 'hold, book, cancel, reschedule' },
    { scope: 'clients:read', module: 'clients', grants: 'identity, visits, lifetime value' },
    {
        scope: 'clients:preferences',
        module: 'clients',
        grants: 'adds preferences, tags and notes (health-adjacent — §6.6)',
    },
    { scope: 'clients:write', module: 'clients', grants: 'editing a guest profile' },
    { scope: 'certificates:read', module: 'certificates', grants: 'certificate balances' },
    { scope: 'credit:read', module: 'credit', grants: 'account credit balances' },
    { scope: 'products:read', module: 'products', grants: 'the retail catalog' },
    { scope: 'menu:read', module: 'ordering', grants: 'the prepared-item menu' },
    { scope: 'orders:read', module: 'ordering', grants: 'order state' },
    { scope: 'orders:write', module: 'ordering', grants: 'placing an order' },
    {
        scope: 'departures:read',
        module: 'departures',
        grants: 'upcoming sailings and seats remaining',
    },
    /**
     * Lodging's own scope, and it is worth saying what it does **not** gate.
     *
     * A stay's availability and its booking ride the endpoints that already exist
     * — `POST /availability` and `POST /bookings`, with `date_from`/`date_to` read
     * as check-in and check-out (v1.1 §2.6) — so they are `calendar:read` and
     * `calendar:write` and adding a second gate in front of them would be a
     * second answer to "may this key see the book". What this scope is *for* is
     * the lodging-only facets §4.7 names and this wave does not build: the rate
     * calendar as a read, the folio, the external-calendar state. It is here for
     * the reason `certificates:read` and `menu:read` are — so that the day one is
     * sold, the answer is a scope that already has a module attached rather than
     * an ad-hoc string invented under deadline.
     */
    { scope: 'lodging:read', module: 'lodging', grants: 'room types, rates and stay state' },
    /**
     * The payments facet (spec §3.7). **`payments:write` and not
     * `payments:links`**, because §3.3's own table already names it — "`payments:write`
     * (§3.7, §5.1.1 tier 3)" — and the vocabulary is `module:action`, not
     * `module:noun`. A pay link is minted, which is a write; that the only shape
     * of the write available to a partner is a link is what the *capability*
     * field says (`payments: "links"`), and saying it twice in two vocabularies
     * is how two declarations of one fact begin.
     *
     * On module `billing` — §3.1's "Billing and payments", which depends on
     * `sales`. That dependency is the honest one: a deposit is money on a date
     * (§7.4 rule 1) and a tenant with no ledger has nowhere to record it.
     */
    { scope: 'payments:write', module: 'billing', grants: 'minting a pay link, priced by referent' },
    /*
     * There is deliberately **no `fulfillment:*` scope**, and it is the `photos`
     * decision restated: decision 9 of `docs/fulfillment-design.md` says "the
     * partner wire is untouched. No change document; if the concierge ever sells
     * t-shirts it gets a v1.x doc first." A scope exists to be granted to a
     * partner key, and a grantable scope with no route behind it is a declaration
     * shipped ahead of its agreement — the state 0019 went to some trouble to
     * make impossible.
     */
];
export const SCOPE_KEYS = SCOPES.map((s) => s.scope);
export function scopeSpec(scope) {
    return SCOPES.find((s) => s.scope === scope);
}
/**
 * The key aiconcierge holds (spec §3.3) — and, as importantly, what it does not:
 * no `clients:write`, and nothing at all from products, sales, certificates,
 * credit, billing or reporting.
 *
 * Named `…_LIST` rather than `CONCIERGE_SCOPES` on purpose: `packages/store`'s
 * seed fixtures export a constant by that name, and a file that imported both
 * would be reading two different things — the spec's statement and one test
 * tenant's key — under one identifier.
 */
export const CONCIERGE_SCOPE_LIST = [
    'catalog:read',
    'calendar:read',
    'calendar:write',
    'clients:read',
    'clients:preferences',
];
/**
 * The modules the partner path needs, in dependency order.
 *
 * `partner_api` depends on `calendar`, which depends on `clients` and
 * `services`. It is the shortest true path to "taking partner bookings" (plan,
 * MVP 5 acceptance) — and because every surface validates dependencies against
 * the state a request would *leave behind*, all four can go in one call.
 *
 * It lived in `apps/admin/src/vocabulary.ts` for one wave, correctly, while it
 * was only the set the wizard pre-ticked — which is presentation, and dev.md §6
 * keeps presentation app-local. It stopped being presentation when
 * `apps/admin-api`'s **Enable concierge** action began switching exactly these
 * four on, server-side, as step 1 of the provisioning sequence. Two copies of
 * "what the concierge needs", one in a browser bundle and one in the service
 * that acts on it, is the shape every other promotion into this file has
 * already been made to undo.
 */
export const PARTNER_PATH_MODULES = [
    'clients',
    'services',
    'calendar',
    'partner_api',
];
//# sourceMappingURL=modules.js.map